import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, Coins } from 'lucide-react';
import useApi from '../api';
import { formatCredits } from './format';
import type { BillingBalance, BillingSummary } from './types';

interface CreditBannerProps {
  onAddCredits: () => void;
  /** Agent-builder icon rail: render a single icon button instead of the card. */
  collapsed?: boolean;
}

/**
 * Left-nav credit state card (CREDIT_PLANS_PLAN.md D10). Shown only when
 * the org is credit-enforced AND exhausted or at or below 20% remaining.
 * Lives in the sidebar rather than a fixed top strip so it never covers
 * page headers. Re-fetched on every route change so a top-up or plan
 * change clears it without a reload.
 */
const CreditBanner: React.FC<CreditBannerProps> = ({ onAddCredits, collapsed = false }) => {
  const { get } = useApi();
  const location = useLocation();
  const [balance, setBalance] = useState<BillingBalance | null>(null);
  const [isCreditsPlan, setIsCreditsPlan] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await get('/billing/summary');
        const summary = response?.data as BillingSummary | undefined;
        if (!cancelled && summary) {
          setBalance(summary.balance ?? null);
          setIsCreditsPlan(!!summary.is_credits_plan);
        }
      } catch {
        // Billing is best-effort here; the page must render without it.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (!balance) return null;
  return (
    <CreditNotice
      balance={balance}
      isCreditsPlan={isCreditsPlan}
      onAddCredits={onAddCredits}
      collapsed={collapsed}
    />
  );
};

interface CreditNoticeProps extends CreditBannerProps {
  balance: BillingBalance;
  isCreditsPlan: boolean;
}

/** Pure render of the card for a given balance; null when nothing to warn about. */
export const CreditNotice: React.FC<CreditNoticeProps> = ({
  balance,
  isCreditsPlan,
  onAddCredits,
  collapsed = false,
}) => {
  if (!balance.enforced) return null;

  const exhausted = balance.exhausted;
  const low = !exhausted && balance.granted > 0 && balance.remaining / balance.granted <= 0.2;
  if (!exhausted && !low) return null;

  const Icon = exhausted ? AlertTriangle : Coins;
  const title = exhausted ? 'Out of credits' : `${formatCredits(balance.remaining)} credits left`;
  const detail = exhausted
    ? isCreditsPlan
      ? 'Website agent hidden and background runs paused until your next cycle or a top-up.'
      : 'Website agent hidden and background runs paused until you choose a monthly amount.'
    : `of ${formatCredits(balance.granted)}. Agents pause at zero.`;
  const cta = isCreditsPlan ? 'Add credits' : 'Choose a plan';
  const fraction = balance.granted > 0 ? Math.min(Math.max(balance.remaining / balance.granted, 0), 1) : 0;

  const accent = exhausted ? 'text-destructive' : 'text-amber-600 dark:text-amber-400';

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onAddCredits}
        aria-label={`${title}. ${cta}`}
        className={`mx-auto flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-accent/50 ${accent}`}
        title={`${title}. ${cta}`}
      >
        <Icon className="size-4" strokeWidth={1.75} />
      </button>
    );
  }

  return (
    <div
      role="status"
      className={`flex flex-col gap-2 rounded-lg border px-2.5 py-2.5 ${
        exhausted ? 'border-destructive/40 bg-destructive/5' : 'border-amber-500/40 bg-amber-500/5'
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`size-4 shrink-0 ${accent}`} strokeWidth={1.75} />
        <span className={`truncate text-[13px] font-medium ${accent}`}>{title}</span>
      </div>
      {!exhausted && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-amber-500" style={{ width: `${fraction * 100}%` }} />
        </div>
      )}
      <p className="text-[11px] leading-snug text-muted-foreground">{detail}</p>
      <button
        type="button"
        onClick={onAddCredits}
        className={`h-7 w-full rounded-md text-[12px] font-medium transition-colors ${
          exhausted
            ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            : 'border border-border bg-background hover:bg-accent'
        }`}
      >
        {cta}
      </button>
    </div>
  );
};

export default CreditBanner;
